import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Input, Select } from 'antd';
import { DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Controller } from 'react-hook-form';

import { useSelector } from 'react-redux';
import GetField from '../Components/GetField';
import styles from '../styles';

const AntSelect = GetField(Select);
const AntInput = GetField(Input);
const { Option } = Select;

const AccessoryForm = props => {
  const {
    classes,
    getValues,
    setValue,
    control,
    watch,
    accessoryItem,
    accessoryIndex,
    accessoryAppend,
    accessoryRemove,
    bpoNo,
  } = props;

  const accessoriesTable = useSelector(
    state => state.rpoItemsForStoreOutwardInputTable.accessories
  );
  const accessoriesStocksForOutward = useSelector(
    state => state.accessoriesStocksForOutward
  );

  const accessoriesArray = useMemo(() => {
    if (accessoriesTable.productNames.length) {
      const productId = getValues(`accessories[${accessoryIndex}].productId`);

      const productIndex = accessoriesTable.productIndexes[productId];

      if (productIndex !== undefined) {
        return accessoriesTable.accessories[productIndex] || [];
      }
    }
    return [];
  }, [accessoriesTable, watch(`accessories[${accessoryIndex}].productId`)]);

  const factoryArray = useMemo(() => {
    return (
      accessoriesStocksForOutward.factories[
        `P${watch(`accessories[${accessoryIndex}].productId`)}A${watch(
          `accessories[${accessoryIndex}].accessoryId`
        )}`
      ] || []
    );
  }, [
    accessoriesStocksForOutward.factories,
    watch(`accessories[${accessoryIndex}].accessoryId`),
    watch(`accessories[${accessoryIndex}].productId`),
  ]);

  const rackArray = useMemo(() => {
    return (
      accessoriesStocksForOutward.racknos[
        `P${watch(`accessories[${accessoryIndex}].productId`)}A${watch(
          `accessories[${accessoryIndex}].accessoryId`
        )}F${watch(`accessories[${accessoryIndex}].factoryId`)}`
      ] || []
    );
  }, [
    accessoriesStocksForOutward.racknos,
    watch(`accessories[${accessoryIndex}].accessoryId`),
    watch(`accessories[${accessoryIndex}].productId`),
    watch(`accessories[${accessoryIndex}].factoryId`),
  ]);

  useEffect(() => {
    setValue(
      `accessories[${accessoryIndex}].inventoryQuantity`,
      accessoriesStocksForOutward.stocks[
        `P${watch(`accessories[${accessoryIndex}].productId`)}A${watch(
          `accessories[${accessoryIndex}].accessoryId`
        )}F${watch(`accessories[${accessoryIndex}].factoryId`)}R${watch(
          `accessories[${accessoryIndex}].racknoId`
        )}`
      ] || 0
    );
  }, [
    accessoriesStocksForOutward.stocks,
    watch(`accessories[${accessoryIndex}].accessoryId`),
    watch(`accessories[${accessoryIndex}].productId`),
    watch(`accessories[${accessoryIndex}].factoryId`),
    watch(`accessories[${accessoryIndex}].racknoId`),
  ]);

  return (
    <tr>
      {bpoNo ? (
        <td className={classes.inputTd}>
          <Controller
            name={`accessories[${accessoryIndex}].productId`}
            control={control}
            defaultValue={accessoryItem.productId}
            render={({ field }) => (
              <AntSelect
                placeholder="Select product"
                {...field}
                onChange={value => {
                  field.onChange(value);
                  setValue(`accessories[${accessoryIndex}].factoryId`, null);
                  setValue(`accessories[${accessoryIndex}].racknoId`, null);
                }}
              >
                {accessoriesTable.productNames.map(({ product, id }) => (
                  <Option key={id} value={id}>
                    {product}
                  </Option>
                ))}
              </AntSelect>
            )}
          />
        </td>
      ) : null}
      <td className={classes.inputTd}>
        <Controller
          name={`accessories[${accessoryIndex}].accessoryId`}
          control={control}
          defaultValue={accessoryItem.accessoryId}
          render={({ field }) => (
            <AntSelect
              placeholder="Select accessory"
              {...field}
              onChange={value => {
                field.onChange(value);
                setValue(`accessories[${accessoryIndex}].factoryId`, null);
                setValue(`accessories[${accessoryIndex}].racknoId`, null);
              }}
            >
              {accessoriesArray.map(accessory => (
                <Option key={accessory.id} value={accessory.id}>
                  {accessory.accessory}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`accessories[${accessoryIndex}].unit`}
          control={control}
          render={({ field }) => (
            <AntSelect
              disabled
              placeholder="Unit"
              {...{
                ...field,
                value: watch(`accessories[${accessoryIndex}].accessoryId`),
              }}
            >
              {accessoriesArray.map(accessory => (
                <Option key={accessory.id} value={accessory.id}>
                  {accessory.unit}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`accessories[${accessoryIndex}].factoryId`}
          control={control}
          defaultValue={accessoryItem.factoryId}
          render={({ field }) => (
            <AntSelect
              placeholder="Select factory"
              {...field}
              onChange={value => {
                field.onChange(value);
                setValue(`accessories[${accessoryIndex}].racknoId`, null);
              }}
            >
              {factoryArray.map(factory => (
                <Option key={factory.id} value={factory.id}>
                  {factory.name}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`accessories[${accessoryIndex}].racknoId`}
          control={control}
          defaultValue={accessoryItem.racknoId}
          render={({ field }) => (
            <AntSelect placeholder="Selecy rack" {...field}>
              {rackArray.map(rack => (
                <Option key={rack.id} value={rack.id}>
                  {rack.name}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`accessories[${accessoryIndex}].inventoryQuantity`}
          control={control}
          defaultValue={accessoryItem.inventoryQuantity}
          render={({ field }) => (
            <AntInput
              {...field}
              placeholder="Inventory Qty"
              disabled
              type="number"
              min={0}
              size="small"
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`accessories[${accessoryIndex}].quantity`}
          control={control}
          defaultValue={accessoryItem.quantity}
          render={({ field }) => (
            <AntInput
              {...field}
              placeholder="Quantity"
              type="number"
              min={0}
              size="small"
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`accessories[${accessoryIndex}].rate`}
          control={control}
          render={({ field }) => (
            <AntInput
              {...field}
              placeholder="Rate"
              type="number"
              min={0}
              size="small"
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`accessories[${accessoryIndex}].gst`}
          control={control}
          render={({ field }) => (
            <AntInput
              {...field}
              placeholder="Gst"
              type="number"
              min={0}
              size="small"
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`accessories[${accessoryIndex}].packages`}
          control={control}
          defaultValue={accessoryItem.packages}
          render={({ field }) => <AntInput placeholder="Packages" {...field} />}
        />
      </td>
      <td className={classes.inputTd}>
        <DeleteOutlined
          onClick={() => {
            accessoryRemove(accessoryIndex);
          }}
        />
        &nbsp; &nbsp;
        <PlusCircleOutlined onClick={() => accessoryAppend({})} />
      </td>
    </tr>
  );
};

AccessoryForm.propTypes = {
  classes: PropTypes.object.isRequired,
  getValues: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  control: PropTypes.any.isRequired,
  watch: PropTypes.func.isRequired,
  accessoryRemove: PropTypes.func.isRequired,
  accessoryAppend: PropTypes.func.isRequired,
  accessoryItem: PropTypes.object.isRequired,
  accessoryIndex: PropTypes.number.isRequired,
  bpoNo: PropTypes.string,
};

export default withStyles(styles)(AccessoryForm);
